import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ChevronDown, ChevronUp, LayoutGrid } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ModalState } from '../../app/AppRoutes';
import { useServices } from '../../app/services';
import { todayIso, type DebtSummary } from '../../data/store/store';
import { DEBT_TYPES, type BudgetMember, type Transaction, type TransactionType } from '../../domain/model';
import { formatDayMonth } from '../../i18n/format';
import { parseAmount, type ParsedAmount } from '../../i18n/parseAmount';
import { AmountInput, BottomSheet, Button, CategoryDot, ChoiceList, SegmentedControl, TextField, Tile, TileGrid, series, useFormat, useToast } from '../../ui';
import { PeopleFields } from '../entries/people';
import { ConvertedAmount, PaidInField, useConversion } from '../entries/paidIn';
import { ReadOnlyNotice, useReadOnly } from '../../premium/ReadOnlyNotice';
import { loadAllPicks, loadQuickAddBasics, peekQuickAddBasics, pickKey, type QuickPick } from './quickCategories';

type Step = 'pick' | 'all' | 'amount';
/** What an ordinary (expense) pick can be recorded as, behind "Add details". */
type EntryKind = 'expense' | 'refund' | 'transfer';

/**
 * + → category → amount → save. Built for speed:
 *  - one tap on a category focuses the amount inside that same tap, so the
 *    decimal keypad is up immediately (mobile keyboards open only on a
 *    user gesture);
 *  - Enter/Done on the keypad saves; "Same as last time" saves in one tap;
 *  - date, note, paid by, whose it is and usage stay behind "Add details";
 *  - saving closes the sheet and offers Undo.
 */
export function QuickAddSheet() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { store, activeSpace, identity, language, notifyDataChanged } = useServices();
  const readOnly = useReadOnly(activeSpace?.id);
  const { money } = useFormat();
  const toast = useToast();
  const openedOverScreen = Boolean((location.state as ModalState)?.background);
  const close = () => (openedOverScreen ? navigate(-1) : navigate('/', { replace: true }));

  // A warm start from the shell's cache: the grid is there on the first frame.
  const warmStart = activeSpace ? peekQuickAddBasics(activeSpace.id) : undefined;
  const [picks, setPicks] = useState<QuickPick[] | null>(warmStart?.picks ?? null);
  const [all, setAll] = useState<{ category: QuickPick; subs: QuickPick[] }[] | null>(null);
  const [members, setMembers] = useState<BudgetMember[]>(warmStart?.members ?? []);
  const [me, setMe] = useState<BudgetMember | null>(warmStart?.me ?? null);

  const [step, setStep] = useState<Step>('pick');
  const [picked, setPicked] = useState<QuickPick | null>(null);
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState<ParsedAmount>(null);
  const [touched, setTouched] = useState(false);
  const [last, setLast] = useState<Transaction | null>(null);
  const [details, setDetails] = useState(false);
  const [date, setDate] = useState(() => todayIso());
  const [note, setNote] = useState('');
  const [paidBy, setPaidBy] = useState('');
  /** 'shared' or a member id; empty = the budget's default (shared, or the payer's own in a personal budget). */
  const [belongsTo, setBelongsTo] = useState('');
  const [usage, setUsage] = useState('');
  const [provider, setProvider] = useState('');
  const [providers, setProviders] = useState<string[]>([]);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [remaining, setRemaining] = useState('');
  const [rate, setRate] = useState('');
  const [installments, setInstallments] = useState('');
  const [debtKnown, setDebtKnown] = useState<DebtSummary | null>(null);
  const [cardCounts, setCardCounts] = useState<'counts' | 'tracked' | null>(null);
  const [cardTouched, setCardTouched] = useState(false);
  /** The user picked the card option in this sheet: history no longer overrides it. */
  const cardChosen = useRef(false);
  const [kind, setKind] = useState<EntryKind>('expense');
  /** Under details: paid in another currency (the amount typed is in it). */
  const [paidIn, setPaidIn] = useState(activeSpace?.currency ?? 'USD');
  const [conversionTouched, setConversionTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const amountRef = useRef<HTMLInputElement>(null);

  const spaceId = activeSpace?.id;

  useEffect(() => {
    // Warm: the shell keeps the cache current, and reloading here could move a tile under a finger.
    if (!spaceId || peekQuickAddBasics(spaceId)) return;
    let live = true;
    void loadQuickAddBasics(store, spaceId, identity.session.userId).then((b) => {
      if (!live) return;
      setPicks(b.picks);
      setMembers(b.members);
      setMe(b.me);
    });
    return () => {
      live = false;
    };
  }, [store, spaceId, identity]);

  const labelOf = (p: QuickPick) => p.name ?? t(p.labelKey ?? 'categories.other');

  /** The tap that picks a category also focuses the amount: that is what raises the keypad on phones. */
  function choose(p: QuickPick) {
    flushSync(() => {
      setPicked(p);
      setStep('amount');
      setText('');
      setParsed(null);
      setTouched(false);
      setFailed(false);
      setProvider('');
      setPeriodStart('');
      setPeriodEnd('');
      setDueDate('');
      setUsage('');
      setRemaining('');
      setRate('');
      setInstallments('');
      setDebtKnown(null);
      setKind('expense');
      // Known before the tap (warm cache): "Same as last time" is there on the first frame.
      setLast((spaceId && peekQuickAddBasics(spaceId)?.last.get(pickKey(p))) || null);
    });
    amountRef.current?.focus({ preventScroll: true });
    if (spaceId) {
      void store.transactions.lastFor(spaceId, p.categoryId, p.subcategoryId).then((x) => setLast(x ?? null));
      void store.transactions.providersFor(spaceId, p.categoryId, p.subcategoryId).then(setProviders);
      if (p.type === 'credit_card_payment') rememberCard(null);
      if (DEBT_TYPES.includes(p.type)) {
        // What we know about this loan: the rate stays, one installment fewer than last time.
        void store.debt.summaryFor(spaceId, { categoryId: p.categoryId, subcategoryId: p.subcategoryId }).then((s) => {
          setDebtKnown(s);
          if (s.interestRateBp) setRate(String(s.interestRateBp.value / 100));
          if (s.installmentsLeft) setInstallments(String(Math.max(0, s.installmentsLeft.value - 1)));
        });
      }
    }
  }

  /** Preselect how this card's bill was counted last time (never over the user's own tap). */
  function rememberCard(forProvider: string | null) {
    if (!spaceId) return;
    void store.transactions.lastCardChoice(spaceId, forProvider).then((counts) => {
      if (counts === undefined || cardChosen.current) return;
      setCardCounts(counts ? 'counts' : 'tracked');
    });
  }

  async function openAll() {
    if (spaceId && !all) setAll(await loadAllPicks(store, spaceId));
    setStep('all');
  }

  const budgetCurrency = activeSpace?.currency ?? 'USD';
  const payCurrency = details ? paidIn : budgetCurrency;
  const conversion = useConversion(typeof parsed === 'number' ? parsed : null, payCurrency, budgetCurrency, details ? date : todayIso());

  function changePaidIn(next: string) {
    setPaidIn(next);
    setParsed(parseAmount(text, next));
    conversion.reset();
  }

  const isCard = picked?.type === 'credit_card_payment';
  /** The type saved: a refund or transfer only when chosen under details, else the pick's own type. */
  const entryType: TransactionType | undefined = picked?.type === 'expense' && details ? kind : picked?.type;

  async function save(amountOverride?: number) {
    if (!picked || !spaceId || !me || saving) return;
    const amount = amountOverride ?? (typeof parsed === 'number' ? parsed : null);
    if (!amount || amount <= 0) {
      setTouched(true);
      amountRef.current?.focus();
      return;
    }
    if (isCard && !cardCounts) {
      setCardTouched(true);
      return;
    }
    if (details && (periodInvalid || debtInvalid)) return;
    // Paid in another currency: the entry holds its value in the budget's currency, and what was paid.
    const foreign = conversion.foreign && !amountOverride;
    if (foreign && conversion.result.amountMinor === null) {
      setConversionTouched(true);
      return;
    }
    const entryAmount = foreign ? conversion.result.amountMinor! : amount;
    const quantity = usage.trim() ? Number(usage.replace(',', '.')) : null;
    setSaving(true);
    setFailed(false);
    try {
      // An entry for a category that has an open bill this period settles that bill.
      const bill =
        entryType === picked.type
          ? await store.recurring.openBillFor(spaceId, picked.categoryId, picked.subcategoryId, details ? date : todayIso())
          : undefined;
      const tx = await store.transactions.create({
        spaceId,
        categoryId: picked.categoryId,
        subcategoryId: picked.subcategoryId,
        amountMinor: entryAmount,
        original: foreign ? { amountMinor: amount, currency: payCurrency } : null,
        addedBy: me.id,
        type: entryType,
        countsAsSpending: isCard ? cardCounts === 'counts' : undefined,
        date: details ? date : undefined,
        notes: details && note.trim() ? note.trim() : null,
        paidBy: details && paidBy ? paidBy : undefined,
        ownership: details && belongsTo ? (belongsTo === 'shared' ? 'shared' : 'personal') : undefined,
        ownerMemberId: details && belongsTo && belongsTo !== 'shared' ? belongsTo : undefined,
        consumption:
          details && quantity !== null && Number.isFinite(quantity) && picked.unit
            ? { quantity, unit: picked.unit, periodStart: periodStart || null, periodEnd: periodEnd || null }
            : undefined,
        provider: details && provider.trim() ? provider.trim() : null,
        periodStart: details && periodStart ? periodStart : null,
        periodEnd: details && periodEnd ? periodEnd : null,
        dueDate: details && dueDate ? dueDate : null,
        recurringTemplateId: bill && bill.type === entryType ? bill.id : null,
        debt: details && isDebt ? { remainingBalanceMinor: remainingMinor, interestRateBp: rateBp, installmentsLeft: installmentsCount } : undefined,
      });
      notifyDataChanged();
      const label = labelOf(picked);
      close();
      toast.show({
        message: t(entryType === 'refund' ? 'quickAdd.savedRefund' : entryType === 'transfer' ? 'quickAdd.savedTransfer' : 'quickAdd.saved', {
          label,
          amount: money(entryAmount),
        }),
        action: {
          label: t('quickAdd.undo'),
          onClick: () => {
            void store.transactions.remove(tx.id, me.id).then(() => {
              notifyDataChanged();
              toast.show({ message: t('quickAdd.removed') });
            });
          },
        },
      });
    } catch (e) {
      console.error('[quick-add]', e);
      setFailed(true);
      setSaving(false);
    }
  }

  const periodInvalid = Boolean(periodStart && periodEnd && periodEnd < periodStart);

  // Loan and card details (brief §13): all optional, checked only when typed.
  const isDebt = Boolean(picked && DEBT_TYPES.includes(picked.type));
  const isLoan = picked?.type === 'debt_payment';
  const remainingParsed = remaining.trim() ? parseAmount(remaining, activeSpace?.currency ?? 'USD') : null;
  const remainingMinor = typeof remainingParsed === 'number' ? remainingParsed : null;
  const rateNumber = rate.trim() ? Number(rate.trim().replace(',', '.').replace('%', '')) : null;
  const rateBp = rateNumber !== null && Number.isFinite(rateNumber) && rateNumber >= 0 && rateNumber <= 1000 ? Math.round(rateNumber * 100) : null;
  const installmentsCount = /^\d{1,4}$/.test(installments.trim()) && Number(installments) <= 1200 ? Number(installments) : null;
  const remainingError = remainingParsed === 'invalid' ? t('quickAdd.invalid') : undefined;
  const rateError = rate.trim() && rateBp === null ? t('quickAdd.debt.invalidRate') : undefined;
  const installmentsError = installments.trim() && installmentsCount === null ? t('quickAdd.debt.invalidCount') : undefined;
  const debtInvalid = isDebt && Boolean(remainingError || rateError || installmentsError);
  const known = (v: { value: number; date: string } | null | undefined, text: (n: number) => string) =>
    v ? t('quickAdd.debt.last', { value: text(v.value), date: formatDayMonth(v.date, language) }) : undefined;

  const amountError = parsed === 'invalid' ? t('quickAdd.invalid') : touched && (parsed === null || parsed === 0) ? t('quickAdd.required') : undefined;

  const lastHint = useMemo(() => {
    if (!last) return undefined;
    return t('quickAdd.last', { amount: money(last.amountMinor), date: formatDayMonth(last.date, language) });
  }, [last, money, language, t]);

  /* ------------------------------------------------------------ steps */

  // A shared budget whose owner's Premium ended: nothing can be added now; say so instead of a form that fails.
  if (readOnly && activeSpace) {
    return (
      <BottomSheet open onClose={close} title={t('nav.add')}>
        <ReadOnlyNotice space={activeSpace} />
      </BottomSheet>
    );
  }

  if (step !== 'amount' || !picked) {
    return (
      <BottomSheet open onClose={close} title={step === 'all' ? t('quickAdd.allTitle') : t('nav.add')}>
        {step === 'pick' ? (
          <div className="stack stack-tight">
            <TileGrid label={t('quickAdd.pickLabel')}>
              {(picks ?? []).map((p, i) => (
                <Tile key={p.id} leading={<CategoryDot color={series(p.colorSlot)} />} label={labelOf(p)} onClick={() => choose(p)} autoFocus={i === 0} />
              ))}
            </TileGrid>
            <Button variant="ghost" icon={LayoutGrid} onClick={() => void openAll()}>
              {t('quickAdd.allCategories')}
            </Button>
          </div>
        ) : (
          <div className="stack">
            {(all ?? []).map(({ category, subs }) => (
              <section key={category.id} className="qa-group">
                <Tile leading={<CategoryDot color={series(category.colorSlot)} />} label={labelOf(category)} onClick={() => choose(category)} />
                {subs.length > 0 && (
                  <TileGrid label={labelOf(category)}>
                    {subs.map((s) => (
                      <Tile key={s.id} label={labelOf(s)} onClick={() => choose(s)} />
                    ))}
                  </TileGrid>
                )}
              </section>
            ))}
          </div>
        )}
      </BottomSheet>
    );
  }

  return (
    <BottomSheet
      open
      onClose={close}
      title={labelOf(picked)}
      footer={
        <>
          <Button variant="primary" size="lg" block loading={saving} onClick={() => void save()} data-testid="quick-add-save">
            {t('quickAdd.save')}
          </Button>
          <Button variant="ghost" size="lg" block icon={details ? ChevronUp : ChevronDown} onClick={() => setDetails((d) => !d)} aria-expanded={details}>
            {details ? t('quickAdd.hideDetails') : t('quickAdd.addDetails')}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="qa-amount">
          <AmountInput
            label={t('quickAdd.amount')}
            value={text}
            currency={payCurrency}
            inputRef={amountRef}
            onValueChange={(v, p) => {
              setText(v);
              setParsed(p);
              conversion.reset();
            }}
            onSubmit={() => void save()}
            hint={conversion.foreign ? undefined : lastHint}
            error={amountError}
          />
          {conversion.foreign && <ConvertedAmount conversion={conversion} paidIn={payCurrency} budgetCurrency={budgetCurrency} showError={conversionTouched} />}
          {last && !text && !conversion.foreign && (
            <Button variant="secondary" onClick={() => void save(last.amountMinor)} data-testid="quick-add-same">
              {t('quickAdd.sameAsLast', { amount: money(last.amountMinor) })}
            </Button>
          )}
          <button type="button" className="link-button" onClick={() => setStep('pick')}>
            {t('quickAdd.changeCategory')}
          </button>
        </div>

        {isCard && (
          <div className="stack stack-xs">
            <p className="field-label">{t('quickAdd.card.label')}</p>
            <ChoiceList<'counts' | 'tracked'>
              label={t('quickAdd.card.label')}
              value={cardCounts}
              onChange={(v) => {
                cardChosen.current = true;
                setCardCounts(v);
                setCardTouched(false);
              }}
              options={[
                { value: 'counts', label: t('quickAdd.card.counts'), description: t('quickAdd.card.countsBody') },
                { value: 'tracked', label: t('quickAdd.card.tracked'), description: t('quickAdd.card.trackedBody') },
              ]}
            />
            {cardTouched && !cardCounts && (
              <p className="field-msg field-error" role="alert">
                {t('quickAdd.card.required')}
              </p>
            )}
          </div>
        )}

        {details && (
          <div className="stack qa-details" role="group" aria-label={t('quickAdd.detailsLabel')}>
            {picked.type === 'expense' && (
              <div className="stack stack-xs">
                <SegmentedControl<EntryKind>
                  label={t('quickAdd.kind.label')}
                  value={kind}
                  onChange={setKind}
                  options={[
                    { value: 'expense', label: t('quickAdd.kind.expense') },
                    { value: 'refund', label: t('quickAdd.kind.refund') },
                    { value: 'transfer', label: t('quickAdd.kind.transfer') },
                  ]}
                />
                <p className="field-msg">{t(`quickAdd.kind.${kind}Body`)}</p>
              </div>
            )}
            <TextField label={t('quickAdd.date')} type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
            <PaidInField budgetCurrency={budgetCurrency} value={paidIn} onChange={changePaidIn} />
            <PeopleFields
              members={members}
              paidBy={paidBy || me?.id || ''}
              belongsTo={belongsTo || (activeSpace?.type === 'personal' ? (me?.id ?? '') : 'shared')}
              onPaidBy={setPaidBy}
              onBelongsTo={setBelongsTo}
            />
            <TextField
              label={isDebt ? t('quickAdd.debt.lender') : t('quickAdd.provider')}
              hint={isDebt ? t('quickAdd.debt.lenderHint') : t('quickAdd.providerHint')}
              value={provider}
              maxLength={60}
              autoComplete="off"
              list={providers.length ? 'qa-providers' : undefined}
              onChange={(e) => {
                setProvider(e.target.value);
                if (isCard && e.target.value.trim()) rememberCard(e.target.value);
              }}
            />
            {providers.length > 0 && (
              <datalist id="qa-providers">
                {providers.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            )}
            {picked.unit && (
              <TextField
                label={t('quickAdd.usage', { unit: picked.unit })}
                hint={t('quickAdd.usageHint')}
                inputMode="decimal"
                value={usage}
                onChange={(e) => setUsage(e.target.value)}
                error={usage.trim() && typeof parseAmount(usage, 'USD') !== 'number' ? t('quickAdd.invalid') : undefined}
              />
            )}
            {isDebt && (
              <>
                <TextField
                  label={t('quickAdd.debt.remaining')}
                  hint={known(debtKnown?.remainingBalanceMinor, (n) => money(n)) ?? t('quickAdd.debt.remainingHint')}
                  inputMode="decimal"
                  autoComplete="off"
                  value={remaining}
                  onChange={(e) => setRemaining(e.target.value)}
                  error={remainingError}
                />
                <div className="field-pair">
                  <TextField
                    label={t('quickAdd.debt.rate')}
                    inputMode="decimal"
                    autoComplete="off"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    error={rateError}
                  />
                  <TextField
                    label={t('quickAdd.debt.installments')}
                    hint={known(debtKnown?.installmentsLeft, String)}
                    inputMode="numeric"
                    autoComplete="off"
                    value={installments}
                    onChange={(e) => setInstallments(e.target.value)}
                    error={installmentsError}
                  />
                </div>
              </>
            )}
            {!isLoan && (
              <div className="field-pair">
                <TextField label={t('quickAdd.periodStart')} type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
                <TextField
                  label={t('quickAdd.periodEnd')}
                  type="date"
                  value={periodEnd}
                  min={periodStart || undefined}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  error={periodInvalid ? t('quickAdd.periodError') : undefined}
                />
              </div>
            )}
            <TextField label={t('quickAdd.dueDate')} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            <TextField label={t('quickAdd.note')} value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
          </div>
        )}

        {failed && (
          <p className="field-msg field-error" role="alert">
            {t('quickAdd.error')}
          </p>
        )}
      </div>
    </BottomSheet>
  );
}
